import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Stack, Theme, Typography, alpha } from '@mui/material';
import { useFormikContext } from 'formik';
import { EmployeeOffboardingAppointmentHandling } from '@prisma/client';
import useConfirm from '../../../hooks/useConfirm';
import { ROUTES } from '../../../constants/global';
import { OFFBOARDING_WIZARD_STEPS } from '../../../constants/offboardings';
import {
  isOffboardingStepComplete,
  resolveStepHelperText,
  reviewStepForEdit,
} from '../../../helpers/offboardings';
import { OffboardingWizardValues, StepId } from '../../../types/offboardings';
import { stepEnter } from '../../../helpers/styles/offboardings';
import HelperText from './HelperText';
import WhenStep from './steps/WhenStep';
import AppointmentsStep from './steps/AppointmentsStep';
import AccessStep from './steps/AccessStep';
import ReviewStep from './steps/ReviewStep';

interface Props {
  employeeName: string;
  isEdit: boolean;
  isSelf: boolean;
}

function renderStepBody(
  stepId: StepId,
  args: {
    employeeName: string;
    goTo: (id: StepId) => void;
  },
) {
  switch (stepId) {
    case 'when':
      return <WhenStep />;
    case 'appointments':
      return <AppointmentsStep employeeName={args.employeeName} />;
    case 'access':
      return <AccessStep employeeName={args.employeeName} />;
    case 'review':
    default:
      return <ReviewStep goTo={args.goTo} />;
  }
}

function WizardShell({ employeeName, isEdit, isSelf }: Props) {
  const navigate = useNavigate();
  const confirm = useConfirm();

  const { values, errors, isValid, dirty, submitForm, isSubmitting } =
    useFormikContext<OffboardingWizardValues>();

  const visibleSteps = React.useMemo(
    () =>
      OFFBOARDING_WIZARD_STEPS.map(s =>
        s.id === 'review' && isEdit ? reviewStepForEdit(s) : s,
      ),
    [isEdit],
  );

  const [currentStepId, setCurrentStepId] = React.useState<StepId>(() => {
    if (isEdit) return 'review';
    return visibleSteps[0]?.id ?? 'when';
  });
  // Set when the user jumps to a step from Review via Edit. Continue should
  // then snap straight back to Review, and Back is suppressed.
  const [returningToReview, setReturningToReview] = React.useState(false);

  React.useEffect(() => {
    if (!visibleSteps.some(s => s.id === currentStepId)) {
      setCurrentStepId(visibleSteps[visibleSteps.length - 1]?.id ?? 'when');
    }
  }, [currentStepId, visibleSteps]);

  const currentIndex = visibleSteps.findIndex(s => s.id === currentStepId);
  const currentStep = visibleSteps[currentIndex] ?? visibleSteps[0];
  const isLast = currentIndex === visibleSteps.length - 1;
  const stepComplete = isOffboardingStepComplete(
    currentStep.id,
    values,
    errors as Record<string, unknown>,
  );

  const helperText = resolveStepHelperText(currentStep, {
    values,
    isEdit,
    employeeName,
  });

  const confirmExitIfDirty = async () => {
    if (!dirty) return true;
    return confirm({
      title: 'Exit without saving?',
      message:
        'You have unsaved changes. Are you sure you want to exit? Any changes you made will be lost.',
      positiveText: 'Exit',
    });
  };

  const onClose = async () => {
    if (await confirmExitIfDirty()) navigate(ROUTES.team);
  };
  const onBack = async () => {
    if (currentIndex === 0) {
      if (await confirmExitIfDirty()) navigate(ROUTES.team);
      return;
    }
    setCurrentStepId(visibleSteps[currentIndex - 1].id);
  };
  const onNext = async () => {
    if (returningToReview) {
      const reviewIdx = visibleSteps.findIndex(s => s.id === 'review');
      if (reviewIdx >= 0) {
        setCurrentStepId(visibleSteps[reviewIdx].id);
        setReturningToReview(false);
        return;
      }
    }
    if (isLast) {
      if (values.mode === 'IMMEDIATE') {
        const appointmentsLine =
          values.appointmentHandling ===
          EmployeeOffboardingAppointmentHandling.STAY_AT_SHOP
            ? `${employeeName}'s upcoming appointments will become unassigned at the shop.`
            : `${employeeName}'s upcoming appointments will move with them to their next location.`;
        const confirmed = await confirm({
          title: 'Offboard immediately?',
          message: `This will start the offboarding for ${employeeName} right now and remove them from the shop. ${appointmentsLine}`,
          positiveText: 'Offboard now',
        });
        if (!confirmed) return;
      }
      if (isSelf) {
        const confirmed = await confirm({
          title: '⚠️ Warning: Offboarding Yourself',
          message:
            "You're about to offboard yourself from the shop. Once it completes, you will no longer be able to access Porter for this business.",
          positiveText: 'Continue',
        });
        if (!confirmed) return;
      }
      await submitForm();
      return;
    }
    setCurrentStepId(visibleSteps[currentIndex + 1].id);
  };
  const goTo = (id: StepId) => {
    if (currentStepId === 'review' && id !== 'review') {
      setReturningToReview(true);
    }
    setCurrentStepId(id);
  };

  return (
    <Box
      sx={() => ({
        position: 'fixed',
        inset: 0,
        background: '#fff',
        color: 'text.primary',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      })}>
      {/* Top bar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        sx={theme => ({
          p: '28px 48px 0',
          [theme.breakpoints.down(720)]: { p: '20px 20px 0' },
        })}>
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          minWidth={0}
          flex={1}
          sx={{
            typography: 'h5',
          }}>
          <Box
            aria-hidden
            sx={theme => ({
              flexShrink: 0,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: theme.palette.primary.main,
            })}
          />
          <span>{isEdit ? 'Edit offboarding' : 'Offboarding'}</span>
          <Box
            component="span"
            aria-hidden
            sx={theme => ({
              color: theme.palette.outlineGrey.main,
              fontWeight: 'fontWeightRegular',
            })}>
            ·
          </Box>
          <Box
            component="span"
            sx={() => ({
              color: 'text.secondary',
              fontWeight: 'fontWeightMedium',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            })}>
            {employeeName}
          </Box>
        </Stack>
        <Button
          variant="light"
          color="info"
          onClick={onClose}
          sx={{
            flexShrink: 0,
            height: 36,
            borderRadius: '10px',
            typography: 'subtitle2',
          }}>
          Close
        </Button>
      </Stack>

      {/* Stage */}
      <Box
        key={currentStep.id}
        sx={theme => ({
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '32px 48px 40px',
          overflowY: 'auto',
          [theme.breakpoints.down(1024)]: { p: '24px 32px 32px' },
          [theme.breakpoints.down(720)]: { p: '20px 20px 24px' },
        })}>
        <Stack
          gap={3.5}
          sx={{
            width: '100%',
            maxWidth: 640,
            animation: `${stepEnter} 320ms cubic-bezier(0.2, 0.8, 0.2, 1) both`,
          }}>
          <Typography
            variant="overline"
            sx={() => ({
              color: 'text.secondary',
            })}>
            {currentStep.eyebrow}
          </Typography>
          <Typography
            variant="display"
            component="h1"
            sx={theme => ({
              lineHeight: 1.2,
              letterSpacing: '-0.015em',
              color: 'text.primary',
              m: 0,
              [theme.breakpoints.down(720)]: { fontSize: 'h1.fontSize' },
            })}>
            {currentStep.headline}
          </Typography>
          <Typography
            variant="body1"
            sx={() => ({
              lineHeight: 1.6,
              color: 'text.secondary',
              maxWidth: 560,
              m: 0,
            })}>
            {currentStep.lede}
          </Typography>
          <Stack gap={1}>
            {renderStepBody(currentStep.id, {
              employeeName,
              goTo,
            })}
            {helperText ? <HelperText>{helperText}</HelperText> : null}
          </Stack>
        </Stack>
      </Box>

      {/* Bottom bar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        sx={theme => ({
          p: '22px 48px 28px',
          borderTop: `1px solid ${alpha(
            theme.palette.darkerLightGrey.main,
            0.45,
          )}`,
          background: '#fff',
          [theme.breakpoints.down(1024)]: { p: '20px 32px 24px' },
          [theme.breakpoints.down(720)]: {
            p: '14px 20px 18px',
            flexDirection: 'column-reverse',
            alignItems: 'stretch',
            gap: 1.75,
          },
        })}>
        <Stack
          direction="row"
          alignItems="center"
          gap="6px"
          role="progressbar"
          aria-label={`Step ${currentIndex + 1} of ${visibleSteps.length}`}
          aria-valuenow={currentIndex + 1}
          aria-valuemin={1}
          aria-valuemax={visibleSteps.length}
          sx={theme => ({
            flexShrink: 0,
            [theme.breakpoints.down(1024)]: { gap: '4px' },
            [theme.breakpoints.down(720)]: {
              justifyContent: 'center',
              width: '100%',
            },
          })}>
          {visibleSteps.map((s, idx) => {
            const isActive = returningToReview || idx === currentIndex;
            const isDone = returningToReview || idx < currentIndex;
            return (
              <Box
                key={s.id}
                sx={(theme: Theme) => ({
                  display: 'block',
                  height: 4,
                  width: 40,
                  background: theme.palette.darkerLightGrey.main,
                  borderRadius: '2px',
                  overflow: 'hidden',
                  [theme.breakpoints.down(1024)]: { width: 28 },
                  [theme.breakpoints.down(720)]: {
                    flex: 1,
                    width: 'auto',
                    maxWidth: 60,
                  },
                })}>
                <Box
                  sx={(theme: Theme) => ({
                    display: 'block',
                    height: '100%',
                    width: isActive || isDone ? '100%' : '0%',
                    background: theme.palette.primary.main,
                    transition: theme.transitions.create(
                      ['width', 'background-color'],
                      {
                        duration: 320,
                        easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
                      },
                    ),
                  })}
                />
              </Box>
            );
          })}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          gap={1.25}
          sx={theme => ({
            [theme.breakpoints.down(720)]: {
              width: '100%',
              '& > button': { flex: 1, justifyContent: 'center' },
            },
          })}>
          {!returningToReview && !(isEdit && isLast) ? (
            <Button
              variant="light"
              color="info"
              onClick={onBack}
              sx={{
                height: 46,
                borderRadius: '10px',
                px: '22px',
                typography: 'h5',
              }}>
              {currentIndex === 0 ? 'Cancel' : 'Back'}
            </Button>
          ) : null}
          <Button
            variant="base"
            onClick={onNext}
            disabled={!stepComplete || (isLast && (!isValid || isSubmitting))}
            sx={theme => ({
              height: 46,
              borderRadius: '10px',
              px: '22px',
              gap: 1,
              transition: theme.transitions.create(
                ['background-color', 'transform'],
                { duration: 200 },
              ),
              '&:hover': { transform: 'translateY(-1px)' },
              '& .offb-arrow': {
                display: 'inline-block',
                transition: theme.transitions.create(['transform'], {
                  duration: 220,
                  easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
                }),
              },
              '&:hover .offb-arrow': { transform: 'translateX(3px)' },
              typography: 'h5',
            })}>
            {returningToReview
              ? 'Back to review'
              : isLast
              ? isEdit
                ? 'Save changes'
                : 'Begin offboarding'
              : 'Continue'}
            <span className="offb-arrow" aria-hidden>
              →
            </span>
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export default WizardShell;
